import { SatellitePoint } from '../types/satellite';

export interface N2YOSatellite {
  satid: number;
  satname: string;
  satlatitude: number;
  satlongitude: number;
  sataltitude: number;
  azimuth: number;
  elevation: number;
  ra: number;
  dec: number;
  timestamp: number;
}

export interface N2YOResponse {
  info: {
    satname: string;
    satid: number;
    transactionscount: number;
  };
  positions: N2YOSatellite[];
}

export class N2YOService {
  private apiKey = (import.meta as any).env?.VITE_N2YO_API_KEY;
  private baseUrl = 'https://api.n2yo.com/rest/v1/satellite';
  
  constructor() {
    if (!this.apiKey) {
      console.warn('N2YO API key not found. N2YO services will be disabled.');
    }
  }

  /**
   * Get satellite positions from N2YO API
   */
  async getSatellitePositions(
    noradIds: number[],
    observerLat: number = 0,
    observerLng: number = 0,
    observerAlt: number = 0,
    seconds: number = 300
  ): Promise<SatellitePoint[]> {
    if (!this.apiKey) {
      throw new Error('N2YO API key not configured');
    }

    const positions: SatellitePoint[] = [];

    // N2YO API only allows one satellite per request for positions endpoint
    for (const noradId of noradIds.slice(0, 5)) { // Limit to 5 to avoid rate limits
      try {
        const url = `${this.baseUrl}/positions/${noradId}/${observerLat}/${observerLng}/${observerAlt}/${seconds}/&apiKey=${this.apiKey}`;
        
        const response = await fetch(url);
        if (!response.ok) {
          console.warn(`N2YO API error for satellite ${noradId}: ${response.status}`);
          continue;
        }

        const data: N2YOResponse = await response.json();
        
        if (data.positions && data.positions.length > 0) {
          const latest = data.positions[0]; // Get most recent position
          
          positions.push({
            name: data.info.satname || `SAT-${noradId}`,
            lat: latest.satlatitude,
            lng: latest.satlongitude,
            alt: Math.max(0.001, latest.sataltitude / 6371), // Normalize altitude
            size: 0.5,
            color: '#ff6b00', // Orange for N2YO data
          });
        }
      } catch (error) {
        console.warn(`Failed to fetch N2YO data for satellite ${noradId}:`, error);
      }
    }

    return positions;
  }

  /**
   * Get satellite information by NORAD ID
   */
  async getSatelliteInfo(noradId: number): Promise<any> {
    if (!this.apiKey) {
      throw new Error('N2YO API key not configured');
    }

    try {
      const url = `${this.baseUrl}/tle/${noradId}&apiKey=${this.apiKey}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Failed to get satellite info for ${noradId}:`, error);
      throw error;
    }
  }

  /**
   * Get visual passes for a satellite
   */
  async getVisualPasses(
    noradId: number,
    observerLat: number,
    observerLng: number,
    observerAlt: number,
    days: number = 10,
    minVisibility: number = 300
  ): Promise<any> {
    if (!this.apiKey) {
      throw new Error('N2YO API key not configured');
    }

    try {
      const url = `${this.baseUrl}/visualpasses/${noradId}/${observerLat}/${observerLng}/${observerAlt}/${days}/${minVisibility}&apiKey=${this.apiKey}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Failed to get visual passes for ${noradId}:`, error);
      throw error;
    }
  }

  /**
   * Check if N2YO service is available
   */
  isAvailable(): boolean {
    return !!this.apiKey;
  }

  /**
   * Get API usage information
   */
  async getApiInfo(): Promise<any> {
    if (!this.apiKey) {
      throw new Error('N2YO API key not configured');
    }

    try {
      const url = `${this.baseUrl}/transactions&apiKey=${this.apiKey}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get API info:', error);
      throw error;
    }
  }
}